import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { AppState, Platform } from "react-native";
import * as Localization from "expo-localization";
import { apiCall, isNoCredentialResponse } from '@/services/api';
import { storage } from "../services/storage";
import { reportClientError } from "../services/clientErrorReporter";
// Not ./AuthContext: that provider's import chain reaches expo-secure-store, and
// this module is imported across the app through utils/units (authSession.ts).
import { useAuthUserId } from "./authSession";

// Regions where the device-locale-inferred default is imperial.
//
// 🩺 MAY 15 2026 — Reverted "CA" from this set. The May 11 commit
// (8c50ffbf6, workout-timer regression bundle, item #5) added "CA"
// alongside "US" because a subset of Canadian users use lbs for body
// weight. But the inference here drives ALL unit displays — temperature,
// distance, weight — not just body weight. Adding CA flipped every
// Canadian user without an explicit `units` preference to imperial,
// which produced °F temperatures in workout cards (WorkoutWeatherDetail
// .native.tsx:109 reads `units === 'imperial'`) and miles in coaching
// narrative — both wrong for Canada (officially metric since 1970).
//
// Forcing case: p.rush12 2026-05-15 — Ottawa, Canada user opened
// new recalibrated workouts on iOS, saw Fahrenheit in weather strip
// despite being in a metric country.
//
// Correct semantic: country = imperial vs metric default, period. Use
// the user_core_profile.country_code as the source of truth for unit
// preferences. Body-weight-in-lbs for Canadian users who want it can
// be handled via the manual units toggle in Settings (preserved by the
// "explicit server-side units overrides locale default" rule below).
//
// US stays as imperial default (still correct). UK stays metric default
// (hybrid stones/kg handled by manual user override). Liberia + Myanmar
// not in scope — no users yet.
const IMPERIAL_REGIONS = new Set(["US"]);

/**
 * Infer default units from the device's locale. Used only when the
 * user has no explicit `units` value on the server (new users / users
 * who have never visited the units setting). Once a user explicitly
 * picks units it persists and overrides this default.
 */
function inferDefaultUnitsFromLocale(): "metric" | "imperial" {
  try {
    const locales = Localization.getLocales();
    const region = locales?.[0]?.regionCode;
    if (region && IMPERIAL_REGIONS.has(region)) return "imperial";
  } catch {
    // expo-localization unavailable (web SSR / test env) — fall through
  }
  return "metric";
}

const DEFAULT_UNITS = inferDefaultUnitsFromLocale();

interface UserProfile {
  id?: string; // User ID (UUID) for API calls requiring user identification
  firstName?: string;
  lastName?: string;
  preferredName?: string; // 🎯 JAN 19, 2026: Name user told Cedar in chat
  email?: string;
  birthdate?: string; // ISO date string
  age?: number; // Calculated from birthdate
  height?: string;
  weight?: string;
  gender?: string;
  timezone?: string;
  subscriptionStatus?: string; // 🆕 JAN 26, 2026: Beta User, Premium, etc.
  gpsPermissionGranted?: boolean; // 🆕 JAN 26, 2026: Platform GPS permission status
  units: 'metric' | 'imperial';
  // B1 (faithful-completion): granular weight unit surfaced by
  // GET /api/memory/profile from user_core_profile.weight_unit, mapped to a
  // UnitSystem. Present only when FF_COMPLETION_UNIT_FROM_PROFILE is ON;
  // weight surfaces (e.g. the native completion sheet) should prefer this
  // over `units` (which also drives distance and defaults to metric).
  weightUnit?: 'metric' | 'imperial' | 'stones';
  // UNITS-U2 (owner ruling #42): the granular unit preferences, served by
  // GET /api/memory/profile when FF_UNITS_CANONICAL_RESOLVER is on. Read them
  // through resolveUnitPreferences / useUnitPreferences, never directly: an
  // absent field falls back to the device-region guess above (the pre-lane
  // behaviour), which is first-paint only and never persisted.
  distanceUnit?: 'km' | 'miles';
  temperatureUnit?: 'celsius' | 'fahrenheit';
  heightUnit?: 'cm' | 'ft_in';
  // UNITS-U4: served when FF_UNITS_IMPERIAL_RATES opts the athlete in.
  rateUnitsFollowPreference?: boolean;
  // FIX-UNITS-1 item 7: an open-water swim reads in metres for everyone.
  openWaterSwimMetric?: boolean;
  // UNITS-U3: dimensions the one-time card should ask about (absent = none).
  unitsAskOnce?: Array<'distance' | 'temperature' | 'weight' | 'height'>;
  lastUpdated?: string;
}

interface ProfileContextType {
  profile: UserProfile | null;
  loading: boolean;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  updateUnits: (units: 'metric' | 'imperial') => Promise<void>;
  refreshProfile: () => Promise<void>;
  // 🆕 JAN 26, 2026: Individual field update methods
  updatePreferredName: (name: string) => Promise<void>;
  updateBirthDate: (birthdate: string) => Promise<void>;
  updateAge: (age: number) => Promise<void>;
  updateHeight: (height: string) => Promise<void>;
  updateWeight: (weight: string) => Promise<void>;
  updateGPSPermission: (granted: boolean) => Promise<void>;
  // UNITS-U2: set one or more unit dimensions as the athlete's own choice
  // (PUT /api/memory/profile/unit-preferences, UNITS-U1).
  updateUnitPreferences?: (choices: {
    distanceUnit?: 'km' | 'miles';
    weightUnit?: 'kg' | 'lbs' | 'stones';
    temperatureUnit?: 'celsius' | 'fahrenheit';
    heightUnit?: 'cm' | 'ft_in';
  }) => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

type ProfileLoad = { ok: true; profile: UserProfile } | { ok: false; reason: string };

/** Waits before each timed retry of a failed load; after the last, coming on screen retries. */
const PROFILE_RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000, 60_000, 60_000];

/**
 * The athlete's unit choices as the server last gave them, per athlete. The key
 * holds her id, so AuthContext.logout's sweep of her keys removes it.
 */
const REMEMBERED_UNIT_FIELDS = ['weightUnit', 'distanceUnit', 'temperatureUnit', 'heightUnit'] as const;
type RememberedUnits = Partial<Pick<UserProfile, typeof REMEMBERED_UNIT_FIELDS[number]>>;
const rememberedUnitsKey = (userId: string) => `profile_units_v1_${userId}`;

async function readRememberedUnits(userId: string): Promise<RememberedUnits | null> {
  try {
    const raw = await storage.getItem(rememberedUnitsKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const units: RememberedUnits = {};
    for (const field of REMEMBERED_UNIT_FIELDS) {
      if (typeof parsed[field] === 'string') (units as Record<string, unknown>)[field] = parsed[field];
    }
    return units;
  } catch (error) {
    console.error(`❌ PROFILE_UNITS_MEMORY_UNREADABLE error=${(error as Error)?.name ?? 'unknown'}`);
    return null;
  }
}

async function rememberUnits(userId: string, profile: UserProfile): Promise<void> {
  const units: RememberedUnits = {};
  for (const field of REMEMBERED_UNIT_FIELDS) {
    if (profile[field] !== undefined) (units as Record<string, unknown>)[field] = profile[field];
  }
  try {
    await storage.setItem(rememberedUnitsKey(userId), JSON.stringify(units));
  } catch (error) {
    console.error(`❌ PROFILE_UNITS_MEMORY_WRITE_FAILED error=${(error as Error)?.name ?? 'unknown'}`);
  }
}

/**
 * UNITS-U2/U5A: the profile when a ProfileProvider is mounted, else undefined
 * (never throws). Display helpers read units through this so a component
 * rendered outside the provider (tests, isolated previews) keeps its pre-lane
 * output instead of crashing.
 */
export const useOptionalProfile = (): ProfileContextType | undefined => useContext(ProfileContext);

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

interface ProfileProviderProps {
  children: ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const [profile, setProfile] = useState<UserProfile | null>({ units: DEFAULT_UNITS });
  const [loading, setLoading] = useState(true);

  const calculateAge = (birthdate: string): number => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const fetchProfile = async (): Promise<ProfileLoad> => {
    // 🔧 DEV MODE: Skip API calls on localhost (web only). On native, never treat as localhost.
    const isLocalhost = Platform.OS === 'web' && typeof window !== 'undefined' && window.location && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    // Mock mode: URL param (web only) or storage (all platforms)
    let mockMode = false;
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      mockMode = urlParams.get('mock') === 'true';
    }
    if (!mockMode) {
      const storedMock = await storage.getItem('mockMode');
      mockMode = storedMock === 'true';
    }
    
    if (mockMode || isLocalhost) {
      console.log('🎨 Mock mode enabled - using mock profile data', { mockMode, isLocalhost });
      return {
        ok: true,
        profile: {
          firstName: 'Matt',
          lastName: 'Designer',
          email: 'matt@coachcedar.com',
          birthdate: '1990-01-15',
          age: 34,
          height: '180',
          weight: '75',
          gender: 'male',
          timezone: 'America/New_York',
          units: DEFAULT_UNITS,
          lastUpdated: new Date().toISOString(),
        },
      };
    }

    try {
      const response = await apiCall('/api/memory/profile');
      if (isNoCredentialResponse(response)) return { ok: false, reason: 'no_credential' };
      if (!response.ok) return { ok: false, reason: `http_${response.status}` };
      const profileData = await response.json();

      // Calculate age from birthdate if available
      if (profileData.birthdate) {
        profileData.age = calculateAge(profileData.birthdate);
      }

      return {
        ok: true,
        profile: {
          ...profileData,
          // The server never sends the coarse `units`; the region default stands for it.
          units: profileData.units || DEFAULT_UNITS,
        },
      };
    } catch (error) {
      return { ok: false, reason: error instanceof TypeError ? 'network' : String((error as Error)?.name ?? 'unknown') };
    }
  };

  // ── Loading the athlete's profile ────────────────────────────────────────
  // It is loaded for the signed-in athlete (AuthContext's user), loaded again
  // whenever that athlete changes, and a failed load is retried — never
  // replaced by a guess. The guess was the bug: kirsten.brouse 044901ed,
  // 2026-10-14. The one load at launch ran before the keychain answered, got no
  // token, and `{ units: <region> }` took the profile's place — metric on a
  // Canadian phone. She signed in seconds later, nothing loaded the profile
  // again, and the workout she logged was entered in kg.
  const currentUserIdRef = useRef<string | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failedAttemptsRef = useRef(0);
  const loadedFromServerRef = useRef(false);

  const clearRetry = () => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
  };

  const loadForUser = async (userId: string): Promise<void> => {
    setLoading(true);
    const result = await fetchProfile();
    if (currentUserIdRef.current !== userId) return; // signed out or switched meanwhile
    setLoading(false);

    if (result.ok) {
      clearRetry();
      failedAttemptsRef.current = 0;
      loadedFromServerRef.current = true;
      setProfile(result.profile);
      // Mirror the user's STORED timezone into the sync store so the non-React
      // home-widget writer can read the authoritative coaching tz (matches the
      // server reminder calendar; not the device tz). Best-effort.
      try {
        const { setUserTimezone } = await import('../services/userTimezoneSync');
        setUserTimezone(result.profile.timezone);
      } catch { /* best-effort */ }
      return;
    }

    failedAttemptsRef.current += 1;
    const attempt = failedAttemptsRef.current;
    console.error(`❌ PROFILE_LOAD_FAILED reason=${result.reason} attempt=${attempt} — keeping the profile shown, retrying`);
    if (attempt === 1) void reportClientError({ surface: `ProfileLoadFailed.${result.reason}` });
    clearRetry();
    if (attempt <= PROFILE_RETRY_DELAYS_MS.length) {
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        if (currentUserIdRef.current === userId) void loadForUser(userId);
      }, PROFILE_RETRY_DELAYS_MS[attempt - 1]);
    }
    // Past the last timed retry, the next time the app comes on screen retries.
  };

  const refreshProfile = async () => {
    const userId = currentUserIdRef.current;
    if (userId) await loadForUser(userId);
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    try {
      // Calculate age from birthdate if updated
      if (updates.birthdate) {
        updates.age = calculateAge(updates.birthdate);
      }
      
      // 🔧 DEV MODE: Skip API calls on localhost (web only)
      const isLocalhost = Platform.OS === 'web' && typeof window !== 'undefined' && window.location && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      if (isLocalhost) {
        console.log('🔧 DEV MODE: Updating profile locally (no API call)');
        setProfile(prev => ({
          ...prev,
          ...updates,
          units: updates.units || prev?.units || DEFAULT_UNITS
        }));
        return;
      }
      
      const response = await apiCall('/api/memory/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updatedProfile = await response.json();
        
        // Calculate age from birthdate if updated
        if (updatedProfile.birthdate) {
          updatedProfile.age = calculateAge(updatedProfile.birthdate);
        }
        
        setProfile(prev => ({
          ...prev,
          ...updatedProfile,
          units: updatedProfile.units || prev?.units || DEFAULT_UNITS
        }));
      } else {
        throw new Error("Failed to update profile");
      }
    } catch (error) {
      console.error("Failed to update profile:", error);
      // Update locally as fallback for better UX
      setProfile(prev => ({
        ...prev,
        ...updates,
        units: updates.units || prev?.units || DEFAULT_UNITS
      }));
      throw error;
    }
  };

  const updateUnits = async (units: 'metric' | 'imperial') => {
    try {
      // 🔧 DEV MODE: Skip API calls on localhost (web only)
      const isLocalhost = Platform.OS === 'web' && typeof window !== 'undefined' && window.location && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      if (isLocalhost) {
        console.log('🔧 DEV MODE: Updating units locally (no API call)');
        setProfile(prev => ({
          ...prev,
          units,
          // B1: keep the granular weightUnit in sync so weight surfaces that
          // prefer it (completion sheet) don't read a stale value.
          weightUnit: units,
        }));
        return;
      }
      
      const response = await apiCall('/api/memory/profile/units', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ units })
      });

      if (response.ok) {
        setProfile(prev => prev ? { ...prev, units, weightUnit: units } : { units, weightUnit: units });
      } else {
        throw new Error("Failed to update units");
      }
    } catch (error) {
      console.error("Failed to update units:", error);
      // Update units locally even if API fails, for better UX
      setProfile(prev => prev ? { ...prev, units } : { units });
      throw error;
    }
  };

  // 🆕 JAN 26, 2026: Individual field update methods (Mission 7)
  const updatePreferredName = async (name: string) => {
    try {
      const response = await apiCall('/api/memory/profile/preferred-name', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferredName: name })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update preferred name");
      }

      setProfile(prev => prev ? { ...prev, preferredName: name } : { units: DEFAULT_UNITS, preferredName: name });
    } catch (error) {
      console.error("Failed to update preferred name:", error);
      throw error;
    }
  };

  const updateBirthDate = async (birthdate: string) => {
    try {
      const response = await apiCall('/api/memory/profile/birth-date', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ birthdate })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update birth date");
      }

      const result = await response.json();
      setProfile(prev => prev ? { ...prev, birthdate, age: result.age } : { units: DEFAULT_UNITS, birthdate, age: result.age });
    } catch (error) {
      console.error("Failed to update birth date:", error);
      throw error;
    }
  };

  const updateAge = async (age: number) => {
    try {
      const response = await apiCall('/api/memory/profile/age', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ age })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update age");
      }

      setProfile(prev => prev ? { ...prev, age } : { units: DEFAULT_UNITS, age });
    } catch (error) {
      console.error("Failed to update age:", error);
      throw error;
    }
  };

  const updateHeight = async (height: string) => {
    try {
      const response = await apiCall('/api/memory/profile/height', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ height })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update height");
      }

      setProfile(prev => prev ? { ...prev, height } : { units: DEFAULT_UNITS, height });
    } catch (error) {
      console.error("Failed to update height:", error);
      throw error;
    }
  };

  const updateWeight = async (weight: string) => {
    try {
      const response = await apiCall('/api/memory/profile/weight', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weight })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update weight");
      }

      setProfile(prev => prev ? { ...prev, weight } : { units: DEFAULT_UNITS, weight });
    } catch (error) {
      console.error("Failed to update weight:", error);
      throw error;
    }
  };

  const updateGPSPermission = async (granted: boolean) => {
    try {
      const response = await apiCall('/api/memory/profile/gps-permission', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gpsPermissionGranted: granted })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update GPS permission");
      }

      setProfile(prev => prev ? { ...prev, gpsPermissionGranted: granted } : { units: DEFAULT_UNITS, gpsPermissionGranted: granted });
    } catch (error) {
      console.error("Failed to update GPS permission:", error);
      throw error;
    }
  };

  // UNITS-U2: one or more unit dimensions as the athlete's own choice. The
  // server owns the values; local state is updated from what it accepted.
  const updateUnitPreferences: NonNullable<ProfileContextType['updateUnitPreferences']> = async (choices) => {
    const response = await apiCall('/api/memory/profile/unit-preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(choices),
    });
    if (!response.ok) {
      throw new Error('Failed to update unit preferences');
    }
    const accepted = await response.json();
    const weightUnit = accepted.weightUnit === 'lbs' ? 'imperial'
      : accepted.weightUnit === 'stones' ? 'stones'
      : accepted.weightUnit === 'kg' ? 'metric' : undefined;
    setProfile(prev => {
      const base = prev ?? { units: DEFAULT_UNITS };
      return {
        ...base,
        ...(accepted.distanceUnit ? { distanceUnit: accepted.distanceUnit } : {}),
        ...(accepted.temperatureUnit ? { temperatureUnit: accepted.temperatureUnit } : {}),
        ...(accepted.heightUnit ? { heightUnit: accepted.heightUnit } : {}),
        ...(weightUnit ? { weightUnit } : {}),
      };
    });
  };

  const userId = useAuthUserId();

  useEffect(() => {
    currentUserIdRef.current = userId;
    clearRetry();
    failedAttemptsRef.current = 0;
    loadedFromServerRef.current = false;
    // One athlete's profile is never shown to the next, or to no one.
    setProfile({ units: DEFAULT_UNITS });
    if (!userId) {
      setLoading(false);
      return;
    }
    // Her units as the server last gave them, until it answers again — a launch
    // with no signal (a gym) shows her units, not the region's.
    void readRememberedUnits(userId).then((units) => {
      if (units && currentUserIdRef.current === userId && !loadedFromServerRef.current) {
        setProfile((prev) => ({ ...(prev ?? { units: DEFAULT_UNITS }), ...units }));
      }
    });
    void loadForUser(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadForUser reads refs; userId is the trigger
  }, [userId]);

  // A failed load is tried again as soon as the app comes back on screen.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      const current = currentUserIdRef.current;
      if (state === 'active' && current && failedAttemptsRef.current > 0) {
        clearRetry();
        void loadForUser(current);
      }
    });
    return () => {
      subscription.remove();
      clearRetry();
      currentUserIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- subscribe once; it reads refs
  }, []);

  // Remember the athlete's units whenever the server's answer (or her own
  // choice, which the server accepted) changes them.
  useEffect(() => {
    if (!userId || !loadedFromServerRef.current || !profile) return;
    void rememberUnits(userId, profile);
  }, [userId, profile?.weightUnit, profile?.distanceUnit, profile?.temperatureUnit, profile?.heightUnit]); // eslint-disable-line react-hooks/exhaustive-deps

  const contextValue: ProfileContextType = {
    profile,
    loading,
    updateProfile,
    updateUnits,
    refreshProfile,
    // 🆕 JAN 26, 2026: Individual update methods
    updatePreferredName,
    updateBirthDate,
    updateAge,
    updateHeight,
    updateWeight,
    updateGPSPermission,
    updateUnitPreferences,
  };

  return (
    <ProfileContext.Provider value={contextValue}>
      {children}
    </ProfileContext.Provider>
  );
}; 